/**
 * Reemplaza lib/queries.ts + los pedazos de lib/activityApi.ts y lib/reviewApi.ts
 * que ActivitiesList/ActivityDetail realmente usan. Antes era Supabase
 * (.from()/.rpc(), filtrado por RLS); ahora es fetch() contra el Worker,
 * filtrado por src/authz.ts + src/workflow.ts del backend.
 *
 * institutional_dashboard/unit_dashboard y el ciclo de observaciones
 * (agregar/resolver/decidir revision) ya tienen ruta real en el Worker
 * (dashboards.ts, review.ts) -- reconectados aqui. fetchVersionDiff ya NO es
 * un stub: llama a GET /activities/:id/versions/diff y calcula el diff
 * campo-a-campo del lado del cliente, igual que el RPC original
 * `diff_activity_versions` (ver versioning.ts en el backend).
 */
import type { ActivityStatus, EngagementStage } from '@rat/shared';
import { apiDownload, apiFetch, apiUpload } from './backend';

export interface ActivityRow {
  id: string;
  ref_code: string;
  title: string;
  status: ActivityStatus;
  completeness_pct: number;
  responsible_unit_id: string;
  has_sensitive_data: boolean;
  has_international_transfer: boolean;
  has_automated_decision: boolean;
  updated_at: string;
}

export async function fetchActivities(opts: {
  status?: ActivityStatus;
  search?: string;
  unitId?: string;
  limit?: number;
}): Promise<ActivityRow[]> {
  // Filtrado en el cliente sobre /activities (no /activities?unit=): ese
  // endpoint de compatibilidad del Sprint 1 no hace bypass institucional y
  // solo devuelve {id,title}. /activities ya trae exactamente lo que el
  // usuario puede ver -- filtrar por unidad ahi nunca expone una unidad
  // ajena, porque esa fila ya venia autorizada en la respuesta.
  let rows = await apiFetch<ActivityRow[]>('/activities');
  if (opts.status) rows = rows.filter((r) => r.status === opts.status);
  if (opts.unitId) rows = rows.filter((r) => r.responsible_unit_id === opts.unitId);
  if (opts.search) {
    const s = opts.search.toLowerCase();
    rows = rows.filter((r) => r.title.toLowerCase().includes(s) || r.ref_code.toLowerCase().includes(s));
  }
  return rows.slice(0, opts.limit ?? 50);
}

export interface ActivityDetailResponse extends ActivityRow {
  operational_owner_name: string | null;
  purpose: string | null;
  description: string | null;
  data_source: string | null;
  uses_ai: boolean;
  own_systems_text: string | null;
  retention_value: number | null;
  retention_unit: string | null;
  retention_criterion: string | null;
  retention_text: string | null;
  retention_criterion_label: string | null;
  responsible_unit: { name_short: string } | null;
  data_categories: Array<{ is_sensitive: boolean; data_category: { code: string; label: string } }>;
  subject_categories: Array<{ subject_category: { code: string; label: string } }>;
  legal_bases: Array<{ legal_basis: { code: string; label: string; requires_reinforced: boolean } }>;
  recipients: Array<{
    name: string | null;
    is_processor: boolean;
    detail: string | null;
    recipient_type: { code: string; label: string } | null;
  }>;
  transfers: Array<{ country: string; detail: string | null; guarantee_type: { code: string; label: string } | null }>;
  security_measures: Array<{ detail: string | null; security_measure: { code: string; label: string } }>;
  automated_decisions: Array<{
    description: string;
    stage: string | null;
    human_in_the_loop: boolean;
    produces_profiling: boolean;
  }>;
}

export async function fetchActivity(id: string): Promise<ActivityDetailResponse> {
  return apiFetch<ActivityDetailResponse>(`/activities/${id}`);
}

// ── asistente completo: crear + guardar por paso ───────────────────────────

export async function createActivityApi(input: {
  responsible_unit_id: string;
  title: string;
  operational_owner_name?: string;
}): Promise<string> {
  const result = await apiFetch<{ id: string }>('/activities', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return result.id;
}

/** Campos "core" (no colecciones) -- PATCH generico, solo se mandan los presentes. */
export async function updateActivityCore(id: string, fields: Record<string, unknown>): Promise<void> {
  await apiFetch(`/activities/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

/**
 * "Reemplazar todo" por coleccion -- mismo patron que el asistente viejo
 * (guardar un paso borra y reinserta esa coleccion completa, nunca un add
 * incremental). Cada endpoint recibe `{items: [...]}`.
 */
async function replaceCollectionApi(activityId: string, collection: string, items: unknown[]): Promise<void> {
  await apiFetch(`/activities/${activityId}/${collection}`, { method: 'POST', body: JSON.stringify({ items }) });
}

export const saveDataCategories = (activityId: string, items: Array<{ category: string; is_sensitive: boolean }>) =>
  replaceCollectionApi(activityId, 'data-categories', items);

export const saveLegalBases = (activityId: string, items: Array<{ legal_basis: string; is_reinforced: boolean }>) =>
  replaceCollectionApi(activityId, 'legal-bases', items);

export const saveSubjectCategories = (
  activityId: string,
  items: Array<{ category: string; is_protected_group: boolean }>,
) => replaceCollectionApi(activityId, 'subject-categories', items);

export const saveRecipients = (
  activityId: string,
  items: Array<{ recipient_type: string; name?: string; is_processor: boolean; detail?: string }>,
) => replaceCollectionApi(activityId, 'recipients', items);

export const saveTransfers = (
  activityId: string,
  items: Array<{ country: string; guarantee_type?: string; detail?: string }>,
) => replaceCollectionApi(activityId, 'transfers', items);

export const saveSecurityMeasures = (activityId: string, items: Array<{ measure: string; detail?: string }>) =>
  replaceCollectionApi(activityId, 'security-measures', items);

export const saveAutomatedDecisions = (
  activityId: string,
  items: Array<{ description: string; stage?: string; human_in_the_loop: boolean; produces_profiling: boolean }>,
) => replaceCollectionApi(activityId, 'automated-decisions', items);

export interface VersionSummary {
  id: string;
  version_no: number;
  reason: string;
  created_at: string;
}

export async function fetchActivityHistory(id: string): Promise<VersionSummary[]> {
  const rows = await apiFetch<Array<{ version_no: number; reason: string; created_at: string }>>(
    `/activities/${id}/versions`,
  );
  // el backend no asigna un id propio a cada version -- version_no ya es unico por actividad
  return rows.map((r) => ({ ...r, id: String(r.version_no) })).reverse();
}

// ── ciclo de revision: observaciones + decision del DPD ────────────────────

export type ObservationSeverity = 'obligatoria' | 'sugerida';
export interface Observation {
  id: string;
  field_path: string;
  severity: ObservationSeverity;
  text: string;
  created_at: string;
  resolved_at: string | null;
}

export async function fetchOpenObservations(activityId: string): Promise<Observation[]> {
  return apiFetch<Observation[]>(`/activities/${activityId}/observations`);
}

export async function addObservation(
  activityId: string,
  fieldPath: string,
  severity: ObservationSeverity,
  text: string,
): Promise<void> {
  await apiFetch(`/activities/${activityId}/observations`, {
    method: 'POST',
    body: JSON.stringify({ field_path: fieldPath, severity, text }),
  });
}

export async function resolveObservation(observationId: string, resolved: boolean): Promise<void> {
  await apiFetch(`/observations/${observationId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolved }),
  });
}

/**
 * Decision del DPD (aprobar/observar). Deliberadamente distinta de
 * transitionActivity(): pasa por review-decide, que bloquea la aprobacion
 * si quedan observaciones obligatorias sin resolver -- el endpoint generico
 * /status no aplica esa regla.
 */
export async function decideReview(activityId: string, decision: 'approved' | 'observed'): Promise<ActivityStatus> {
  const result = await apiFetch<{ status: ActivityStatus }>(`/activities/${activityId}/review-decide`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
  return result.status;
}

// ── dashboards ──────────────────────────────────────────────────────────

export interface RiskIndicators {
  con_datos_sensibles: number;
  con_transferencia_internacional: number;
  con_decision_automatizada: number;
  observadas_hace_tiempo: number;
}

export interface AttentionRow {
  id: string;
  ref_code: string | null;
  title: string;
  unit_name: string;
  status: string;
  dias_sin_movimiento: number;
}

export interface MonthlyEvolutionRow {
  mes: string;
  creadas: number;
  aprobadas: number;
}

export interface InstitutionalDashboardData {
  unidades_total: number;
  actividades_total: number;
  por_estado: Record<string, number>;
  por_unidad: Array<{
    unit_id: string;
    name: string;
    rat_status: string;
    activities_total: number;
    completeness_avg: number;
  }>;
  riesgo: RiskIndicators;
  atencion: AttentionRow[];
  evolucion_mensual: MonthlyEvolutionRow[];
}

export async function fetchInstitutionalDashboard(): Promise<InstitutionalDashboardData> {
  return apiFetch<InstitutionalDashboardData>('/dashboard/institutional');
}

export interface UnitDashboardData {
  unit_id: string;
  unit_name: string;
  rat_status: string;
  activities_total: number;
  completeness_avg: number;
  by_status: Record<string, number>;
  riesgo: RiskIndicators;
  observaciones_abiertas: number;
  atencion: AttentionRow[];
}

export async function fetchUnitDashboard(unitId: string): Promise<UnitDashboardData> {
  return apiFetch<UnitDashboardData>(`/dashboard/unit/${unitId}`);
}

export async function transitionUnit(unitId: string, to: string): Promise<string> {
  const result = await apiFetch<{ status: string }>(`/units/${unitId}/status`, {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
  return result.status;
}

// ── historial y comparacion de versiones ────────────────────────────────────

export interface VersionDiff {
  scalar: Array<{ field: string; a: unknown; b: unknown }>;
  collections: Array<{ name: string; a: number; b: number }>;
}

/**
 * Campos "de contenido" que vale la pena mostrar en un diff -- se excluyen
 * deliberadamente id/ref_code/responsible_unit_id (nunca cambian),
 * status/completeness_pct/current_version_no/updated_at (son metadata
 * derivada, no algo que el DPD "cambio" a mano) y `data_source`/`title`
 * quedan incluidos porque si son contenido real editable.
 */
const SCALAR_FIELD_LABEL: Record<string, string> = {
  title: 'Título',
  operational_owner_name: 'Responsable operativo',
  purpose: 'Finalidad',
  description: 'Descripción',
  data_source: 'Origen de los datos',
  has_international_transfer: 'Transferencia internacional',
  has_automated_decision: 'Decisiones automatizadas',
  uses_ai: 'Usa inteligencia artificial',
  own_systems_text: 'Sistemas propios',
  retention_value: 'Plazo de conservación',
  retention_unit: 'Unidad del plazo',
  retention_criterion: 'Criterio de conservación',
  retention_text: 'Notas de conservación',
};

const COLLECTION_LABEL: Record<string, string> = {
  legalBases: 'Bases de licitud',
  dataCategories: 'Categorías de datos',
  subjectCategories: 'Categorías de titulares',
  recipients: 'Destinatarios',
  transfers: 'Transferencias',
  securityMeasures: 'Medidas de seguridad',
  automatedDecisions: 'Decisiones automatizadas',
};

interface ActivitySnapshot {
  activity: Record<string, unknown>;
  [collection: string]: unknown;
}

/**
 * El calculo del diff vive ACA, del lado del cliente -- mismo patron que el
 * RPC original `diff_activity_versions`, que tambien devolvia los dos
 * snapshots crudos y dejaba el calculo campo-a-campo a la UI (ver
 * apps/workers-backend/src/versioning.ts). Compara campos escalares
 * (valor distinto) y tamaños de colección (cuantos ítems tenía cada una),
 * no el contenido item-a-item de las colecciones -- suficiente para "qué
 * cambió" sin construir un diff estructural completo.
 */
function computeVersionDiff(a: ActivitySnapshot, b: ActivitySnapshot): VersionDiff {
  const scalar: VersionDiff['scalar'] = [];
  for (const [field, label] of Object.entries(SCALAR_FIELD_LABEL)) {
    const av = a.activity?.[field];
    const bv = b.activity?.[field];
    if (JSON.stringify(av ?? null) !== JSON.stringify(bv ?? null)) {
      scalar.push({ field: label, a: av, b: bv });
    }
  }

  const collections: VersionDiff['collections'] = [];
  for (const [key, label] of Object.entries(COLLECTION_LABEL)) {
    const av = Array.isArray(a[key]) ? (a[key] as unknown[]).length : 0;
    const bv = Array.isArray(b[key]) ? (b[key] as unknown[]).length : 0;
    if (av !== bv) collections.push({ name: label, a: av, b: bv });
  }

  return { scalar, collections };
}

export async function fetchVersionDiff(activityId: string, a: number, b: number): Promise<VersionDiff> {
  const { a: snapA, b: snapB } = await apiFetch<{ a: ActivitySnapshot; b: ActivitySnapshot }>(
    `/activities/${activityId}/versions/diff?a=${a}&b=${b}`,
  );
  return computeVersionDiff(snapA, snapB);
}

export async function transitionActivity(
  activityId: string,
  to: ActivityStatus,
  comment?: string,
): Promise<ActivityStatus> {
  const result = await apiFetch<{ status: ActivityStatus }>(`/activities/${activityId}/status`, {
    method: 'POST',
    body: JSON.stringify({ to, comment }), // comment: el backend aun no lo persiste (ver README)
  });
  return result.status;
}

// ── seguimiento del levantamiento (modulo separado del RAT) ────────────────

export interface EngagementRow {
  unit_id: string;
  unit_name: string;
  stage: EngagementStage;
  contacted_on: string | null;
  meeting_on: string | null;
  pending_items: string | null;
  notes: string | null;
  contacts: number;
  activities: number;
}

export async function fetchTracking(): Promise<EngagementRow[]> {
  return apiFetch<EngagementRow[]>('/tracking');
}

export async function saveEngagement(
  unitId: string,
  patch: Partial<Pick<EngagementRow, 'stage' | 'contacted_on' | 'meeting_on' | 'pending_items' | 'notes'>>,
): Promise<void> {
  await apiFetch(`/tracking/${unitId}`, { method: 'POST', body: JSON.stringify(patch) });
}

export interface Contact {
  id: string;
  unit_id: string;
  full_name: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
}

export async function fetchContacts(unitId: string): Promise<Contact[]> {
  return apiFetch<Contact[]>(`/tracking/${unitId}/contacts`);
}

export async function saveContact(input: {
  unit_id: string;
  full_name: string;
  position?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}): Promise<void> {
  await apiFetch(`/tracking/${input.unit_id}/contacts`, { method: 'POST', body: JSON.stringify(input) });
}

export async function deleteContact(id: string): Promise<void> {
  await apiFetch(`/contacts/${id}/delete`, { method: 'POST' });
}

// ── catalogos administrables (src/catalog-admin.ts en el backend) ──────────

/** Mismo shape que ya usaba el asistente via @rat/shared -- code/label mas la bandera propia del catalogo, si la tiene. */
export interface PublicCatalogItem {
  code: string;
  label: string;
  [flagKey: string]: string | boolean;
}

export type CatalogName =
  | 'legal_basis'
  | 'data_category'
  | 'subject_category'
  | 'recipient_type'
  | 'security_measure'
  | 'transfer_guarantee_type'
  | 'retention_criterion';

export type PublicCatalogs = Record<CatalogName, PublicCatalogItem[]>;

/** GET /catalogs -- cualquier sesion valida, solo items activos. Usado por el asistente. */
export async function fetchCatalogs(): Promise<PublicCatalogs> {
  return apiFetch<PublicCatalogs>('/catalogs');
}

export interface AdminCatalogItem {
  id: string;
  code: string;
  label: string;
  flag: boolean;
  is_active: boolean;
  sort_order: number;
}

export type AdminCatalogs = Record<CatalogName, AdminCatalogItem[]>;

/** GET /admin/catalogs -- institucional solo, incluye items inactivos. */
export async function fetchAdminCatalogs(): Promise<AdminCatalogs> {
  return apiFetch<AdminCatalogs>('/admin/catalogs');
}

export async function createCatalogItem(input: { catalog: CatalogName; code: string; label: string; flag?: boolean }): Promise<void> {
  await apiFetch('/admin/catalogs', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateCatalogItem(
  id: string,
  patch: Partial<{ label: string; flag: boolean; is_active: boolean; sort_order: number }>,
): Promise<void> {
  await apiFetch(`/admin/catalogs/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

// ── consola de administracion de usuarios (src/admin.ts en el backend) ─────

export type UnitRole = 'jefe' | 'colaborador' | 'consulta';

export interface AdminUser {
  id: string;
  email: string;
  is_institutional: boolean;
  is_auditor: boolean;
  mfa_enabled: boolean;
  units: Array<{ unit_id: string; unit_name: string; unit_role: UnitRole }>;
}

/** GET /admin/users -- institucional solo. */
export async function fetchAdminUsers(): Promise<AdminUser[]> {
  return apiFetch<AdminUser[]>('/admin/users');
}

export async function createUserApi(input: {
  email: string;
  password: string;
  is_institutional?: boolean;
  is_auditor?: boolean;
}): Promise<void> {
  await apiFetch('/admin/users', { method: 'POST', body: JSON.stringify(input) });
}

/** Solo is_institutional/is_auditor -- no hay endpoint para cambiar el email. */
export async function updateUserRoles(id: string, patch: Partial<{ is_institutional: boolean; is_auditor: boolean }>): Promise<void> {
  await apiFetch(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function assignUserToUnitApi(userId: string, unitId: string, unitRole: UnitRole): Promise<void> {
  await apiFetch(`/admin/users/${userId}/units`, { method: 'POST', body: JSON.stringify({ unit_id: unitId, unit_role: unitRole }) });
}

export async function removeUserFromUnitApi(userId: string, unitId: string): Promise<void> {
  await apiFetch(`/admin/users/${userId}/units/${unitId}/remove`, { method: 'POST' });
}

export async function resetUserPasswordApi(userId: string, password: string): Promise<void> {
  await apiFetch(`/admin/users/${userId}/reset-password`, { method: 'POST', body: JSON.stringify({ password }) });
}

// ── auditoria (src/audit.ts en el backend) ──────────────────────────────────

export interface AuditEntry {
  id: number;
  occurred_at: string;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  previous_state: string | null;
  new_state: string | null;
}

/** GET /audit -- institucional solo. `beforeId` para "cargar más" (cursor, no offset). */
export async function fetchAuditEntries(opts: { limit?: number; beforeId?: number; action?: string; entityType?: string } = {}): Promise<AuditEntry[]> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.beforeId) params.set('before_id', String(opts.beforeId));
  if (opts.action) params.set('action', opts.action);
  if (opts.entityType) params.set('entity_type', opts.entityType);
  const qs = params.toString();
  return apiFetch<AuditEntry[]>(`/audit${qs ? `?${qs}` : ''}`);
}

export interface AuditChainResult {
  ok: boolean;
  brokenAtId: number | null;
  rowsChecked: number;
}

/** GET /audit/verify -- institucional solo. */
export async function fetchAuditVerify(): Promise<AuditChainResult> {
  return apiFetch<AuditChainResult>('/audit/verify');
}

// ── importacion asistida con staging y curacion (src/import.ts) ────────────

export type ImportKind = 'activity_seed' | 'contact';

export type ImportRowError = 'missing_title' | 'missing_full_name' | 'missing_unit' | 'forbidden';

export interface ImportRowResult {
  row: number;
  ok: boolean;
  id?: string;
  ref_code?: string;
  error?: ImportRowError;
}

export interface ImportActivityRowInput {
  unit_id: string;
  title: string;
  description?: string;
  operational_owner_name?: string;
}

export interface ImportContactRowInput {
  unit_id: string;
  full_name: string;
  position?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

/**
 * El mapeo de columnas, el parseo del archivo y la resolucion de unidad ya
 * ocurrieron en el cliente -- ver lib/importApi.ts y pages/Import.tsx.
 * `columnMapping` (encabezado de origen -> campo IMPORT_FIELDS, sin incluir
 * "unidad", que se resuelve aparte) viaja junto al lote para que el backend
 * la "aprenda" solo si el lote crea algo -- ver getColumnMappings() en
 * src/import.ts.
 */
export async function importRowsApi(
  kind: ImportKind,
  rows: ImportActivityRowInput[] | ImportContactRowInput[],
  columnMapping?: Record<string, string>,
): Promise<ImportRowResult[]> {
  const result = await apiFetch<{ results: ImportRowResult[] }>('/activities/import', {
    method: 'POST',
    body: JSON.stringify({ kind, rows, column_mapping: columnMapping }),
  });
  return result.results;
}

/** Correspondencia de columnas aprendida de lotes anteriores (compartida entre usuarios de la institucion). */
export async function fetchColumnMappings(kind: ImportKind): Promise<Record<string, string>> {
  const result = await apiFetch<{ mapping: Record<string, string> }>(`/activities/import/column-mappings?kind=${kind}`);
  return result.mapping;
}

// ── adjuntos de actividad (Cloudflare R2, src/attachments.ts) ──────────────

export interface AttachmentRow {
  id: string;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export async function fetchAttachments(activityId: string): Promise<AttachmentRow[]> {
  const result = await apiFetch<{ attachments: AttachmentRow[] }>(`/activities/${activityId}/attachments`);
  return result.attachments;
}

export async function uploadAttachmentApi(activityId: string, file: File): Promise<{ id: string }> {
  const form = new FormData();
  form.set('file', file);
  return apiUpload<{ ok: true; id: string }>(`/activities/${activityId}/attachments`, form);
}

export async function deleteAttachmentApi(activityId: string, attachmentId: string): Promise<void> {
  await apiFetch(`/activities/${activityId}/attachments/${attachmentId}`, { method: 'DELETE' });
}

/** Dispara la descarga del navegador -- el archivo nunca tiene URL publica, siempre pasa por fetch() con el token. */
export async function downloadAttachment(activityId: string, attachmentId: string, fallbackFilename: string): Promise<void> {
  const { blob, filename } = await apiDownload(`/activities/${activityId}/attachments/${attachmentId}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? fallbackFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
